/**
 * Fallback Classifier
 * 
 * CRITICAL: This ensures EVERY item gets enrichment fields.
 * 
 * No item should ever reach Smart Insights, Home, Focus pages,
 * or summarizers with missing themes, topics, or summaries.
 * 
 * This is the safety net that prevents crashes and blank insights.
 */

/**
 * THEME CLASSIFICATION RULES
 * 
 * Maps keywords to Swirl's controlled theme vocabulary
 */
const THEME_KEYWORDS = {
  fashion: [
    'outfit', 'shirt', 'jacket', 'pants', 'aesthetic', 'style', 'neutral',
    'clothes', 'wear', 'dress', 'coat', 'blazer', 'sneakers', 'shoes',
    'wardrobe', 'capsule', 'layering', 'fit', 'menswear', 'womenswear',
    'streetwear', 'minimal', 'oversized', 'tailored', 'denim', 'tshirt',
  ],
  
  fitness: [
    'workout', 'gym', 'reps', 'routine', 'arms', 'back', 'protein',
    'exercise', 'training', 'muscle', 'biceps', 'chest', 'legs', 'cardio',
    'strength', 'hypertrophy', 'push', 'pull', 'squat', 'bench', 'deadlift',
    'mobility', 'recovery', 'splits', 'sets', 'weight', 'barbell', 'dumbbell',
  ],
  
  travel: [
    'japan', 'hotel', 'itinerary', 'flights', 'restaurant', 'map', 'trip',
    'tokyo', 'paris', 'barcelona', 'london', 'travel', 'vacation', 'airport',
    'packing', 'backpack', 'hostel', 'airbnb', 'destination', 'city', 'country',
    'tour', 'sightseeing', 'temple', 'museum', 'garden', 'cafe',
  ],
  
  business: [
    'sales', 'objections', 'pitch', 'negotiation', 'marketing', 'founder',
    'startup', 'revenue', 'customer', 'client', 'meeting', 'deal', 'close',
    'pipeline', 'email', 'cold', 'outreach', 'lead', 'prospect', 'discovery',
    'framework', 'strategy', 'business', 'entrepreneur', 'growth', 'b2b',
  ],
  
  sales: [
    'objection', 'pitch', 'close', 'deal', 'prospect', 'lead', 'cold email',
    'discovery', 'qualifying', 'pipeline', 'crm', 'outreach', 'follow-up',
    'value prop', 'negotiation', 'pricing', 'demo', 'call', 'script',
  ],
  
  food: [
    'recipe', 'restaurant', 'cooking', 'meal', 'dessert', 'dinner', 'lunch',
    'breakfast', 'kitchen', 'chef', 'dish', 'cuisine', 'ingredients', 'food',
    'pasta', 'pizza', 'sushi', 'ramen', 'bake', 'grill', 'oven', 'prep',
  ],
  
  tech: [
    'phone', 'camera', 'ai', 'app', 'software', 'gadget', 'laptop', 'iphone',
    'android', 'apple', 'google', 'notion', 'obsidian', 'tool', 'productivity',
    'workflow', 'automation', 'api', 'code', 'programming', 'extension',
  ],
  
  'self improvement': [
    'mental', 'habits', 'focus', 'improvement', 'self', 'mindset', 'goals',
    'journal', 'meditation', 'morning', 'routine', 'discipline', 'growth',
    'personal', 'development', 'mindfulness', 'awareness', 'intention',
  ],
  
  relationships: [
    'dating', 'partner', 'communication', 'love', 'boundaries', 'relationship',
    'conflict', 'intimacy', 'connection', 'friendship', 'family', 'conversation',
    'listening', 'empathy', 'attachment', 'therapy', 'counseling',
  ],
  
  home: [
    'interior', 'bedroom', 'decor', 'desk', 'design', 'furniture', 'room',
    'apartment', 'house', 'setup', 'minimalist', 'aesthetic', 'cozy',
    'organization', 'storage', 'shelving', 'lighting', 'plant',
  ],
  
  health: [
    'health', 'nutrition', 'diet', 'vitamins', 'supplements', 'sleep',
    'recovery', 'injury', 'pain', 'doctor', 'medical', 'wellness', 'immune',
    'hydration', 'calories', 'macro', 'protein', 'carbs', 'fat',
  ],
  
  productivity: [
    'productivity', 'workflow', 'system', 'organization', 'planning', 'calendar',
    'schedule', 'time', 'management', 'efficiency', 'tasks', 'todo', 'gtd',
    'project', 'deadline', 'prioritize', 'focus', 'deep work',
  ],
  
  creativity: [
    'design', 'creative', 'art', 'drawing', 'painting', 'photography',
    'video', 'editing', 'photoshop', 'illustrator', 'figma', 'typography',
    'color', 'composition', 'inspiration', 'moodboard', 'aesthetic',
  ],
  
  career: [
    'career', 'job', 'interview', 'resume', 'linkedin', 'networking',
    'promotion', 'salary', 'negotiation', 'manager', 'leadership', 'team',
    'workplace', 'remote', 'freelance', 'consulting', 'professional',
  ],
  
  money: [
    'money', 'investing', 'stocks', 'crypto', 'savings', 'budget', 'finance',
    'wealth', 'passive income', 'real estate', 'portfolio', 'retirement',
    'tax', 'accountant', 'bank', 'credit', 'debt', 'loan',
  ],
  
  learning: [
    'learn', 'course', 'tutorial', 'education', 'study', 'book', 'reading',
    'notes', 'knowledge', 'skill', 'practice', 'mastery', 'certification',
    'university', 'college', 'online', 'udemy', 'coursera',
  ],
};

/**
 * IMAGE FILENAME PATTERNS
 * 
 * Extract theme hints from image filenames and URLs
 */
const IMAGE_PATTERNS = {
  fashion: /\b(shirt|jacket|pants|outfit|style|wear|clothes|sneaker|shoe|dress|coat|wardrobe)\b/i,
  fitness: /\b(gym|workout|exercise|muscle|training|fitness|lift|squat|bench)\b/i,
  travel: /\b(tokyo|japan|paris|hotel|travel|trip|city|destination|temple|garden)\b/i,
  food: /\b(food|recipe|pizza|sushi|ramen|meal|cook|dish|restaurant|cafe)\b/i,
  home: /\b(bedroom|interior|desk|room|decor|furniture|setup|apartment)\b/i,
  tech: /\b(phone|camera|laptop|iphone|gadget|device|screen|tech)\b/i,
};

/**
 * Check if item needs fallback classification
 * IMPORTANT: This must be declared BEFORE applyFallbackClassification
 */
function needsFallback(item) {
  const reasons = [];
  
  // Check for missing or empty fields
  if (!item.title || item.title.trim().length === 0) {
    reasons.push('missing title');
  }
  
  if (!item.description || item.description.trim().length === 0) {
    reasons.push('missing description');
  }
  
  if (!item.textContent || item.textContent.trim().length < 40) {
    reasons.push('insufficient text content');
  }
  
  // Image exists but no extracted text
  if (item.imageUrl && (!item.textContent || item.textContent.length < 20)) {
    reasons.push('no OCR text from image');
  }
  
  // No auto tags
  if (!item.autoTags || (Array.isArray(item.autoTags) && item.autoTags.length === 0)) {
    reasons.push('no auto tags');
  }
  
  // Missing AI metadata
  if (!item.extractedThemes || item.extractedThemes.length === 0) {
    reasons.push('no themes');
  }
  
  if (!item.microTopics || item.microTopics.length === 0) {
    reasons.push('no microtopics');
  }
  
  // No embedding
  if (!item.embedding) {
    reasons.push('no embedding');
  }
  
  return {
    needsFallback: reasons.length > 0,
    reasons,
  };
}

/**
 * THEME CLASSIFICATION (Fallback)
 * 
 * Classify item into themes using keyword matching
 */
function classifyThemes(text, imageUrl = null, filename = null) {
  const scores = {};
  
  // Initialize scores
  Object.keys(THEME_KEYWORDS).forEach(theme => {
    scores[theme] = 0;
  });
  
  // A. Text-based classification
  if (text && text.length > 0) {
    const lowerText = text.toLowerCase();
    
    Object.entries(THEME_KEYWORDS).forEach(([theme, keywords]) => {
      keywords.forEach(keyword => {
        if (lowerText.includes(keyword)) {
          scores[theme] += 1;
        }
      });
    });
  }
  
  // B. Image-based classification (filename/URL)
  if (imageUrl || filename) {
    const imageText = [imageUrl, filename].filter(Boolean).join(' ').toLowerCase();
    
    Object.entries(IMAGE_PATTERNS).forEach(([theme, pattern]) => {
      if (pattern.test(imageText)) {
        scores[theme] += 2; // Give image hints higher weight
      }
    });
  }
  
  // Get top themes
  const sortedThemes = Object.entries(scores)
    .filter(([_, score]) => score > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([theme]) => theme);
  
  // Return top 1-2 themes
  if (sortedThemes.length > 0) {
    return sortedThemes.slice(0, 2);
  }
  
  // C. Absolute fallback
  return ['lifestyle'];
}

/**
 * MICROTOPIC EXTRACTION (Fallback)
 * 
 * Extract 1-4 microtopics using best-effort inference
 */
function extractMicrotopics(text, themes, imageUrl = null, filename = null) {
  const topics = [];
  
  // A. Text-based extraction
  if (text && text.length > 10) {
    const lowerText = text.toLowerCase();
    
    // Extract quoted phrases
    const quotes = lowerText.match(/"([^"]+)"/g);
    if (quotes) {
      topics.push(...quotes.map(q => q.replace(/"/g, '').trim()).slice(0, 2));
    }
    
    // Extract theme-specific keywords
    themes.forEach(theme => {
      const keywords = THEME_KEYWORDS[theme] || [];
      keywords.forEach(keyword => {
        if (lowerText.includes(keyword) && !topics.includes(keyword)) {
          topics.push(keyword);
        }
      });
    });
    
    // Extract capitalized phrases (potential entities/topics)
    const words = text.split(/\s+/);
    words.forEach(word => {
      if (word.length > 3 && word[0] === word[0].toUpperCase() && !topics.includes(word.toLowerCase())) {
        topics.push(word.toLowerCase());
      }
    });
  }
  
  // B. Image-based extraction
  if ((imageUrl || filename) && topics.length < 3) {
    const imageText = [imageUrl, filename].filter(Boolean).join(' ');
    
    // Extract from filename
    if (filename) {
      const cleanName = filename
        .replace(/\.[^.]+$/, '') // Remove extension
        .replace(/[_-]/g, ' ')    // Replace separators
        .toLowerCase();
      
      const parts = cleanName.split(/\s+/).filter(p => p.length > 3);
      topics.push(...parts.slice(0, 2));
    }
    
    // Add generic image topics if still empty
    if (topics.length === 0) {
      themes.forEach(theme => {
        topics.push(`${theme} visual`);
      });
    }
  }
  
  // C. Absolute fallback
  if (topics.length === 0) {
    return ['saved item'];
  }
  
  // Clean and dedupe
  const cleaned = [...new Set(topics)]
    .filter(t => t.length > 2)
    .slice(0, 4);
  
  return cleaned.length > 0 ? cleaned : ['saved item'];
}

/**
 * SUMMARY GENERATION (Fallback)
 * 
 * Generate a reasonable summary based on available info
 */
function generateSummary(themes, microtopics, text = null) {
  // A. If we have text, use template with specifics
  if (text && text.length > 20) {
    const preview = text.substring(0, 100).trim();
    return `This item covers ${themes.join(' and ')} with details about ${microtopics.slice(0, 2).join(' and ')}.`;
  }
  
  // B. If only image exists
  if (microtopics.some(t => t.includes('visual') || t.includes('image'))) {
    return `This appears to be a ${themes[0]}-related image containing ${microtopics.filter(t => !t.includes('visual')).slice(0, 2).join(' and ')}.`;
  }
  
  // C. Generic fallback with themes
  if (themes.length > 0 && themes[0] !== 'lifestyle') {
    return `This item relates to ${themes.join(' and ')} based on saved context.`;
  }
  
  // D. Absolute minimum
  return 'This item has limited details, but it relates to your saved interests.';
}

/**
 * ENTITY EXTRACTION (Fallback)
 * 
 * Extract brands, locations, numbers, product names
 */
function extractEntities(text) {
  if (!text || text.length < 10) {
    return [];
  }
  
  const entities = [];
  
  // Extract capitalized words (potential proper nouns)
  const words = text.split(/\s+/);
  words.forEach(word => {
    // Must start with capital, length > 2, not common words
    if (
      word.length > 2 && 
      word[0] === word[0].toUpperCase() &&
      !['The', 'This', 'That', 'When', 'Where', 'What', 'How', 'Why'].includes(word)
    ) {
      entities.push(word);
    }
  });
  
  // Extract numbers with context (e.g., "12 reps", "$50")
  const numberMatches = text.match(/\b\d+\s*\w+/g);
  if (numberMatches) {
    entities.push(...numberMatches.slice(0, 2));
  }
  
  // Dedupe and limit
  return [...new Set(entities)].slice(0, 10);
}

/**
 * CALCULATE CONFIDENCE SCORES
 */
function calculateConfidence(item, hasText, hasImage, fallbackReasons) {
  let score = 0.1; // Base minimum
  
  // Strong signals
  if (hasText && item.textContent?.length > 100) {
    score = 0.6;
  } else if (hasText && item.textContent?.length > 40) {
    score = 0.4;
  }
  
  // Image adds confidence
  if (hasImage) {
    score += 0.1;
  }
  
  // Title/description add confidence
  if (item.title && item.title.length > 10) {
    score += 0.1;
  }
  
  if (item.description && item.description.length > 20) {
    score += 0.1;
  }
  
  // Many fallback reasons = lower confidence
  if (fallbackReasons.length > 3) {
    score = Math.max(0.1, score - 0.2);
  }
  
  // Cap at reasonable fallback max
  score = Math.min(score, 0.7);
  
  return {
    themes: score,
    microtopics: score,
    summary: score,
  };
}

/**
 * MAIN FALLBACK CLASSIFIER
 * 
 * This is called when primary enrichment fails or is incomplete.
 * 
 * GUARANTEES: Every item gets themes, microtopics, summary, entities.
 */
export function applyFallbackClassification(item, options = {}) {
  if (import.meta.env.DEV) console.log('[Fallback] Applying fallback classification for:', item.title?.substring(0, 50) || item.id);
  
  // CRITICAL: Declare needsFallback check BEFORE using it
  const fallbackCheck = needsFallback(item);
  const shouldApplyFallback = options.needsFallback ?? fallbackCheck.needsFallback;
  const reasons = fallbackCheck.reasons;
  
  // If item already has complete data, return it
  if (!shouldApplyFallback && item.extractedThemes?.length > 0 && item.microTopics?.length > 0) {
    if (import.meta.env.DEV) console.log('[Fallback] Item already complete, skipping fallback');
    return item;
  }
  
  // Gather all available text
  const allText = [
    item.title,
    item.description,
    item.textContent,
  ].filter(Boolean).join(' ').trim();
  
  const hasText = allText.length > 0;
  const hasImage = !!(item.imageUrl || item.image_url);
  const title = item.title || 'Untitled';
  const channel = item.channel || 'Upload';
  
  // Extract filename from URL
  let filename = null;
  if (item.url || item.imageUrl) {
    try {
      const url = new URL(item.url || item.imageUrl);
      filename = url.pathname.split('/').pop();
    } catch (e) {
      // Invalid URL
    }
  }
  
  // STEP 1: Classify themes (GUARANTEE non-empty array)
  let extractedThemes = Array.isArray(item.extractedThemes) 
    ? item.extractedThemes.filter(Boolean) 
    : [];
  
  if (extractedThemes.length === 0) {
    extractedThemes = classifyThemes(allText, item.imageUrl, filename);
  }
  
  // ABSOLUTE GUARANTEE: Never empty
  if (!Array.isArray(extractedThemes) || extractedThemes.length === 0) {
    extractedThemes = ['lifestyle'];
  }
  
  // STEP 2: Extract microtopics (GUARANTEE non-empty array)
  let microTopics = Array.isArray(item.microTopics) 
    ? item.microTopics.filter(Boolean) 
    : [];
  
  if (microTopics.length === 0) {
    microTopics = extractMicrotopics(allText, extractedThemes, item.imageUrl, filename);
  }
  
  // ABSOLUTE GUARANTEE: Never empty
  if (!Array.isArray(microTopics) || microTopics.length === 0) {
    microTopics = [title || channel || 'saved item'];
  }
  
  // STEP 3: Generate summary (GUARANTEE non-empty string)
  let summary = item.aiAnalysis?.summary && item.aiAnalysis.summary.length > 20
    ? item.aiAnalysis.summary
    : generateSummary(extractedThemes, microTopics, allText);
  
  // ABSOLUTE GUARANTEE: Never empty or too short
  if (!summary || summary.length < 20) {
    summary = `${title} relates to ${extractedThemes.join(' and ')} in ${channel}.`;
  }
  
  // STEP 4: Extract entities (GUARANTEE array, can be empty)
  let entities = Array.isArray(item.entities) 
    ? item.entities.filter(Boolean) 
    : [];
  
  if (entities.length === 0 && allText) {
    entities = extractEntities(allText);
  }
  
  // GUARANTEE: Always an array (but can be empty)
  if (!Array.isArray(entities)) {
    entities = [];
  }
  
  // STEP 5: Calculate confidence
  const confidence = calculateConfidence(item, hasText, hasImage, reasons);
  
  // STEP 6: Build AI analysis (GUARANTEE complete object)
  const existingAnalysis = item.aiAnalysis || {};
  const aiAnalysis = {
    summary: existingAnalysis.summary && existingAnalysis.summary.length >= 20
      ? existingAnalysis.summary
      : summary,
    whyItMatters: existingAnalysis.whyItMatters || 
      `This was saved for reference and relates to your ${extractedThemes[0]} interests.`,
    relatedConcepts: Array.isArray(existingAnalysis.relatedConcepts) && existingAnalysis.relatedConcepts.length > 0
      ? existingAnalysis.relatedConcepts
      : microTopics.slice(0, 3),
  };
  
  // ABSOLUTE GUARANTEE: Summary is never empty
  if (!aiAnalysis.summary || aiAnalysis.summary.length < 20) {
    aiAnalysis.summary = `${title} is stored in your vault.`;
  }
  
  // STEP 7: Build result with GUARANTEED schema compliance
  const result = {
    extractedThemes,      // GUARANTEED: non-empty array
    microTopics,          // GUARANTEED: non-empty array
    entities,             // GUARANTEED: array (can be empty)
    aiAnalysis,           // GUARANTEED: complete object with summary
    confidence,
    meta: {
      ...(item.meta || {}),
      fallbackUsed: true,
      fallbackReasons: reasons,
      fallbackTimestamp: new Date().toISOString(),
    },
  };
  
  if (import.meta.env.DEV) console.log('[Fallback] Classification complete:', {
    themes: result.extractedThemes,
    topics: result.microTopics.length,
    hasAnalysis: !!result.aiAnalysis.summary,
    confidence: confidence.themes,
    reasons: reasons.length,
  });
  
  // FINAL SANITY CHECK
  if (!Array.isArray(result.extractedThemes) || result.extractedThemes.length === 0) {
    console.error('[Fallback] CRITICAL: extractedThemes is empty, forcing default');
    result.extractedThemes = ['lifestyle'];
  }
  
  if (!Array.isArray(result.microTopics) || result.microTopics.length === 0) {
    console.error('[Fallback] CRITICAL: microTopics is empty, forcing default');
    result.microTopics = ['saved item'];
  }
  
  if (!result.aiAnalysis?.summary || result.aiAnalysis.summary.length < 20) {
    console.error('[Fallback] CRITICAL: aiAnalysis.summary is invalid, forcing default');
    result.aiAnalysis.summary = 'This item is stored in your vault.';
  }
  
  return result;
}

/**
 * SMART MERGE
 * 
 * Merge fallback results with existing enrichment.
 * Only fill missing fields, never override good data.
 */
export function mergeFallbackWithEnrichment(enriched, fallback) {
  return {
    extractedThemes: enriched.extractedThemes && enriched.extractedThemes.length > 0
      ? enriched.extractedThemes
      : fallback.extractedThemes,
    
    microTopics: enriched.microTopics && enriched.microTopics.length > 0
      ? enriched.microTopics
      : fallback.microTopics,
    
    entities: enriched.entities && enriched.entities.length > 0
      ? enriched.entities
      : fallback.entities,
    
    aiAnalysis: {
      summary: enriched.aiAnalysis?.summary && enriched.aiAnalysis.summary.length > 20
        ? enriched.aiAnalysis.summary
        : fallback.aiAnalysis.summary,
      
      whyItMatters: enriched.aiAnalysis?.whyItMatters || fallback.aiAnalysis.whyItMatters,
      
      relatedConcepts: enriched.aiAnalysis?.relatedConcepts && enriched.aiAnalysis.relatedConcepts.length > 0
        ? enriched.aiAnalysis.relatedConcepts
        : fallback.aiAnalysis.relatedConcepts,
    },
    
    embedding: enriched.embedding || null,
    
    confidence: fallback.confidence,
    
    meta: {
      ...enriched.meta,
      ...fallback.meta,
      fallbackUsed: fallback.meta.fallbackUsed,
      fallbackReasons: fallback.meta.fallbackReasons,
    },
  };
}

/**
 * Expose for debugging
 */
if (typeof window !== 'undefined') {
  window.fallbackClassifier = {
    needsFallback,
    applyFallbackClassification,
    classifyThemes,
    extractMicrotopics,
  };
  
  if (import.meta.env.DEV) console.log('[Fallback] Debug available: window.fallbackClassifier');
}

export default {
  needsFallback,
  applyFallbackClassification,
  mergeFallbackWithEnrichment,
};

